import { useEffect, useState, type ReactNode } from "react";
import styled, { css } from "styled-components";
import { Button, Color, Flex, Text } from "src/elements";
import { textVariants } from "src/elements/Text/store/variants";
import { useTranslation } from "src/i18n";
import { styledSafe } from "src/utils/styledComponent";
import { ReviewButton } from "../../../pages/SpacePlanning/review/reviewDesign";
import { ReviewColor } from "../../../pages/SpacePlanning/review/reviewDesignTokens";

const PromptElement = styledSafe("textarea")<{ $embedded: boolean }>`
  width: 100%;
  min-height: 64px;
  border: 1px solid ${Color.spaceGray};
  border-radius: 4px;
  padding: 8px 10px;
  font-family: inherit;
  font-size: ${textVariants.body3.fontSize};
  line-height: ${textVariants.body3.lineHeight};
  font-weight: ${textVariants.body3.fontWeight};
  resize: none;
  outline: none;

  :focus {
    border-color: ${Color.primary};
  }
  ${({ $embedded }) =>
    $embedded &&
    css`
      min-height: 48px;
      height: 48px;
      padding: 4px 10px;
      border-color: ${ReviewColor.line};
      border-radius: 10px;
      background: ${ReviewColor.surface};
      color: ${ReviewColor.ink};
      font-size: 13px;
      line-height: 20px;

      ::placeholder {
        color: ${ReviewColor.muted};
      }
      :focus {
        border-color: ${ReviewColor.accent};
        box-shadow: 0 0 0 3px ${ReviewColor.accent}14;
      }
      :disabled {
        background: ${ReviewColor.subtle};
      }
    `}
`;

const Form = styled.form`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  gap: 6px;
  width: 100%;
`;

interface Props {
  embedded?: boolean;
  isRunning: boolean;
  isDisabled?: boolean;
  suggestedPrompt?: { id: string; text: string };
  secondaryAction?: ReactNode;
  onSend: (prompt: string) => void;
}

export const AgentComposer = ({
  embedded = false,
  isRunning,
  isDisabled = false,
  suggestedPrompt,
  secondaryAction,
  onSend,
}: Props) => {
  const { t } = useTranslation("pogAgent");
  const [prompt, setPrompt] = useState("");
  const SendButton = embedded ? ReviewButton : Button;
  useEffect(() => {
    if (suggestedPrompt) setPrompt(suggestedPrompt.text);
  }, [suggestedPrompt]);

  const submit = () => {
    const trimmed = prompt.trim();
    if (!trimmed || isRunning || isDisabled) return;

    setPrompt("");
    onSend(trimmed);
  };

  return (
    <Form
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <PromptElement
        $embedded={embedded}
        data-testid="pog-agent-prompt"
        value={prompt}
        disabled={isDisabled}
        placeholder={t("composer.placeholder")}
        onChange={(event) => setPrompt(event.currentTarget.value)}
        onKeyDown={(event) => {
          if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault();
            submit();
          }
        }}
      />

      <Flex justify="between" align="center" gap="10px">
        {secondaryAction ?? (
          <Text variant="small2" color={Color.textSecondary}>
            {t("composer.hint")}
          </Text>
        )}

        <SendButton
          type="submit"
          size="small"
          isLoading={isRunning}
          isDisabled={isDisabled || !prompt.trim()}
          name="pog-agent-send"
        >
          {t("composer.send")}
        </SendButton>
      </Flex>
    </Form>
  );
};
